import "../css/EditStockist.css";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import Toaster, { showSuccessToast, showErrorToast } from "../dist/Toaster";
import errormessage from "../css/errormessage.css";
import CustomButton from "../pages/CustomButton";

const EditStockist = () => {
  const { stockist_code } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    stockistName: "",
    contactPerson: "",
    country: "India",
    state: "",
    city: "",
    headquarter: "",
    division: "",
    dob: "",
    marriageAnniversary: "",
    address: "",
    mobile: "",
    phone: "",
    email: "",
    pin: "",
    gstin: "",
    panNumber: "",
    dlNumber: "",
    cstNumber: "",
    mappingCode: "",
    active: true,
    added_date: "",
    updated_by: "",
    start_time: "",
    end_time: "",
  });

  const [errors, setErrors] = useState({
    stockistName: "",
    contactPerson: "",
    state: "",
    city: "",
    headquarter: "",
    mobile: "",
    email: "",
    pin: "",
    gstin: "",
    panNumber: "",
    dob: "",
  });

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [headquarters, setHeadquarters] = useState([]);
  const [divisions, setDivisions] = useState([]);

  // Field validation
  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "stockistName":
        if (!value) {
          error = "Stockist name is required";
        } else if (!/^[A-Za-z0-9\s&.,-]+$/.test(value)) {
          error = "Invalid characters in stockist name";
        }
        break;

      case "contactPerson":
        if (!value) {
          error = "Contact person is required";
        } else if (!/^[A-Za-z\s.]+$/.test(value)) {
          error = "Only alphabets and spaces allowed";
        }
        break;

      case "state":
        if (!value) {
          error = "State is required";
        }
        break;

      case "city":
        if (!value) {
          error = "City is required";
        }
        break;

      case "headquarter":
        if (!value) {
          error = "Headquarter is required";
        }
        break;

      case "mobile":
        if (!value) {
          error = "Mobile number is required";
        } else if (!/^\d{10}$/.test(value)) {
          error = "Mobile must be 10 digits";
        }
        break;

      case "email":
        if (
          value &&
          !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(value)
        ) {
          error = "Invalid email format";
        }
        break;

      case "pin":
        if (value && !/^\d{6}$/.test(value)) {
          error = "PIN must be 6 digits";
        }
        break;

      case "gstin":
        if (value && !/^[0-9A-Z]{15}$/.test(value)) {
          error = "GSTIN must be 15 characters";
        }
        break;

      case "panNumber":
        if (value && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)) {
          error = "Invalid PAN format (e.g., ABCDE1234F)";
        }
        break;

      case "dob":
        if (value) {
          const dob = new Date(value);
          const today = new Date();
          let age = today.getFullYear() - dob.getFullYear();
          const monthDiff = today.getMonth() - dob.getMonth();

          if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < dob.getDate())
          ) {
            age--;
          }

          if (age < 18) {
            error = "Must be at least 18 years old";
          }
        }
        break;
    }

    return error;
  };

  // Handle input change with validation
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validate the field
    const error = validateField(name, value);

    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Fetch Stockist Data on Load
  useEffect(() => {
    if (!stockist_code) {
      showErrorToast("Stockist Code is missing!");
      return;
    }

    fetch(`${BASE_URL}/api/stockist/${stockist_code}`, {
      headers: getAuthHeaders(),
    })
      .then((res) => res.json())
      .then((data) => {
        setFormData({
          stockistName: data.stockist_name || "",
          contactPerson: data.contact_person || "",
          country: data.country || "India",
          state: data.state_id || "",
          city: data.city_id || "",
          headquarter: data.headquarter_id || "",
          division: data.division_id || "",
          dob: data.dob || "",
          marriageAnniversary: data.marriage_anniversary || "",
          address: data.address || "",
          mobile: data.mobile || "",
          phone: data.phone || "",
          email: data.email || "",
          pin: data.pin || "",
          gstin: data.gstin || "",
          panNumber: data.pan_number || "",
          dlNumber: data.dl_number || "",
          cstNumber: data.cst_number || "",
          mappingCode: data.mapping_code || "",
          active: data.active || true,
          added_date: data.added_date || "",
          updated_by: data.updated_by || "",
          start_time: data.start_time || "",
          end_time: data.end_time || "",
        });
        if (data.state_id) fetchCities(data.state_id);
        if (data.city_id) fetchHeadquarters(data.city_id);
      })
      .catch((err) => {
        console.error("Error fetching stockist:", err);
        showErrorToast("Failed to load stockist data");
      });
  }, [stockist_code]);

  // Fetch States and Divisions
  useEffect(() => {
    fetch(`${BASE_URL}/api/states`, {
      headers: getAuthHeaders(),
    })
      .then((res) => res.json())
      .then((data) => setStates(data))
      .catch((err) => {
        console.error("Error fetching states:", err);
        showErrorToast("Failed to load states");
      });

    fetch(`${BASE_URL}/api/divisions`, {
      headers: getAuthHeaders(),
    })
      .then((res) => res.json())
      .then((data) => setDivisions(data))
      .catch((err) => {
        console.error("Error fetching divisions:", err);
        showErrorToast("Failed to load divisions");
      });
  }, []);

  // Fetch Cities based on State
  const fetchCities = (state_id) => {
    fetch(`${BASE_URL}/api/cities/${state_id}`, {
      headers: getAuthHeaders(),
    })
      .then((res) => res.json())
      .then((data) => setCities(data))
      .catch((err) => {
        console.error("Error fetching cities:", err);
        showErrorToast("Failed to load cities");
      });
  };

  const fetchHeadquarters = (city_id) => {
    fetch(`${BASE_URL}/api/headquarters/${city_id}`, {
      headers: getAuthHeaders(),
    })
      .then((res) => res.json())
      .then((data) => {
        setHeadquarters(data);
        if (data.length > 0) {
          setFormData((prev) => ({ ...prev, headquarter: data[0].id }));
        }
      })
      .catch((err) => {
        console.error("Error fetching headquarters:", err);
        showErrorToast("Failed to load headquarters");
      });
  };

  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    setFormData({
      ...formData,
      state: selectedState,
      city: "",
      headquarter: "",
    });
    setCities([]);
    setHeadquarters([]);

    if (selectedState) fetchCities(selectedState);
  };

  const handleCityChange = (e) => {
    const selectedCity = e.target.value;
    setFormData({ ...formData, city: selectedCity, headquarter: "" });
    setHeadquarters([]);

    if (selectedCity) {
      fetchHeadquarters(selectedCity);
    }
  };

  // Form validation
  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    const requiredFields = [
      "stockistName",
      "contactPerson",
      "state",
      "city",
      "headquarter",
      "mobile",
    ];

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = "This field is required";
        isValid = false;
      }
    });

    Object.keys(formData).forEach((key) => {
      if (errors[key] || newErrors[key]) return;

      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
        isValid = false;
      }
    });

    setErrors((prev) => ({ ...prev, ...newErrors }));

    // Show first error if any
    if (!isValid) {
      const firstError = Object.values(newErrors).find((error) => error);
      if (firstError) {
        showErrorToast(firstError);
      }
    }

    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showErrorToast("Please fix the errors before submitting");
      return;
    }

    const updatedStockist = {
      stockist_name: formData.stockistName,
      contact_person: formData.contactPerson,
      country: formData.country,
      state_id: parseInt(formData.state, 10),
      city_id: parseInt(formData.city, 10),
      headquarter_id: parseInt(formData.headquarter, 10),
      division_id: formData.division ? parseInt(formData.division, 10) : null,
      mobile: formData.mobile,
      gstin: formData.gstin || null,
      address: formData.address,
      phone: formData.phone || null,
      email: formData.email || null,
      pin: formData.pin || null,
      pan_number: formData.panNumber || null,
      dl_number: formData.dlNumber || null,
      cst_number: formData.cstNumber || null,
      mapping_code: formData.mappingCode || null,
      active: formData.active,
      // added_date: formData.added_date || new Date().toDateString(),
      updated_by: localStorage.getItem("loggedInUser") || "Admin",
      start_time: formData.startTime || new Date().toISOString(),
    };

    try {
      const response = await fetch(
        `${BASE_URL}/api/stockist/${stockist_code}`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify(updatedStockist),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error updating stockist:", errorData);
        showErrorToast(
          "Error updating stockist: " + (errorData.message || "Unknown error")
        );
      } else {
        showSuccessToast("Stockist updated successfully!");
        navigate("/liststockist");
      }
    } catch (error) {
      console.error("Network Error:", error);
      showErrorToast("Failed to connect to the server");
    }
  };

  return (
    <div className="p-2  border-2 border-gray-300 rounded-lg bg-white">
      <div className="stockist-container">
        <h1 class="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span class="text-blue-700">Edit </span>
          <span class="text-blue-500">Stockist</span>
        </h1>

        <form onSubmit={handleSubmit}>
          {/* Basic Details */}
          <fieldset class=" border-2 border-gray-300 rounded-lg bg-gray-50 p-4">
            <legend className="bg-blue-500 border rounded text-white px-3">
              Basic Details
            </legend>
            <div className="grid-container">
              <label className="required-label">STOCKIST NAME</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="stockistName"
                  value={formData.stockistName}
                  onChange={handleChange}
                  required
                  className={errors.stockistName ? "input-error" : ""}
                />
                {errors.stockistName && (
                  <span className="validation-error">
                    {errors.stockistName}
                  </span>
                )}
              </div>

              <label className="required-label">CONTACT PERSON</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  required
                  className={errors.contactPerson ? "input-error" : ""}
                />
                {errors.contactPerson && (
                  <span className="validation-error">
                    {errors.contactPerson}
                  </span>
                )}
              </div>

              <label className="required-label">COUNTRY</label>
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
              >
                <option value="India">INDIA</option>
              </select>

              <label className="required-label">STATE</label>
              <div className="input-field-container">
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleStateChange}
                  required
                  className={errors.state ? "input-error" : ""}
                >
                  <option value="">Select</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
                {errors.state && (
                  <span className="validation-error">{errors.state}</span>
                )}
              </div>

              <label className="required-label">CITY</label>
              <div className="input-field-container">
                <select
                  name="city"
                  value={formData.city}
                  onChange={handleCityChange}
                  required
                  className={errors.city ? "input-error" : ""}
                >
                  <option value="">Select</option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
                {errors.city && (
                  <span className="validation-error">{errors.city}</span>
                )}
              </div>

              <label className="required-label">HQ</label>
              <div className="input-field-container">
                <select
                  name="headquarter"
                  value={formData.headquarter}
                  onChange={handleChange}
                  required
                  className={errors.headquarter ? "input-error" : ""}
                >
                  {headquarters.map((hq) => (
                    <option key={hq.id} value={hq.id}>
                      {hq.name}
                    </option>
                  ))}
                </select>
                {errors.headquarter && (
                  <span className="validation-error">{errors.headquarter}</span>
                )}
              </div>

              <label>Division:</label>
              <select
                name="division"
                value={formData.division}
                onChange={handleChange}
              >
                <option value="">Select</option>
                {divisions.map((div) => (
                  <option key={div.id} value={div.id}>
                    {div.name}
                  </option>
                ))}
              </select>

              <label className="required-label">MOBILE</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  maxLength="10"
                  required
                  className={errors.mobile ? "input-error" : ""}
                />
                {errors.mobile && (
                  <span className="validation-error">{errors.mobile}</span>
                )}
              </div>
            </div>
          </fieldset>

          {/* Contact Details */}
          <fieldset class=" border-2 border-gray-300 rounded-lg bg-gray-50 p-4 mt-2">
            <legend className="bg-blue-500 border rounded text-white px-3">
              Contact Details
            </legend>
            <div className="grid-container">
              <label>ADDRESS:</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
              />

              <label>DATE OF BIRTH</label>
              <div className="input-field-container">
                <input
                  type="date"
                  name="dob"
                  value={formData.dob}
                  onChange={handleChange}
                  className={errors.dob ? "input-error" : ""}
                />
                {errors.dob && (
                  <span className="validation-error">{errors.dob}</span>
                )}
              </div>

              <label>PHONE</label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />

              <label>MARRIAGE ANNIVERSARY:</label>
              <input
                type="date"
                name="marriageAnniversary"
                value={formData.marriageAnniversary}
                onChange={handleChange}
              />

              <label>EMAIL</label>
              <div className="input-field-container">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? "input-error" : ""}
                />
                {errors.email && (
                  <span className="validation-error">{errors.email}</span>
                )}
              </div>

              <label>PIN</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="pin"
                  value={formData.pin}
                  onChange={handleChange}
                  maxLength="6"
                  className={errors.pin ? "input-error" : ""}
                />
                {errors.pin && (
                  <span className="validation-error">{errors.pin}</span>
                )}
              </div>
            </div>
          </fieldset>

          {/* Financial Details */}
          <fieldset class=" border-2 border-gray-300 rounded-lg bg-gray-50 p-4 mt-2">
            <legend className="bg-blue-500 border rounded text-white px-3">
              Financial Details
            </legend>
            <div className="grid-container">
              <label>GSTIN</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="gstin"
                  value={formData.gstin}
                  onChange={handleChange}
                  maxLength="15"
                  className={errors.gstin ? "input-error" : ""}
                />
                {errors.gstin && (
                  <span className="validation-error">{errors.gstin}</span>
                )}
              </div>

              <label>PAN NUMBER</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="panNumber"
                  value={formData.panNumber}
                  onChange={handleChange}
                  maxLength="10"
                  className={errors.panNumber ? "input-error" : ""}
                />
                {errors.panNumber && (
                  <span className="validation-error">{errors.panNumber}</span>
                )}
              </div>

              <label>DL NUMBER</label>
              <input
                type="text"
                name="dlNumber"
                value={formData.dlNumber}
                onChange={handleChange}
              />

              <label>CST NUMBER</label>
              <input
                type="text"
                name="cstNumber"
                value={formData.cstNumber}
                onChange={handleChange}
              />

              <label>MAPPING CODE</label>
              <input
                type="text"
                name="mappingCode"
                value={formData.mappingCode}
                onChange={handleChange}
              />
            </div>
          </fieldset>

          {/* Buttons */}
          <div className="flex items-center justify-center mt-3">
            <CustomButton label="Submit" variant="go" type="submit" />
            <CustomButton
              label="Return"
              variant="cancel"
              onClick={() => navigate("/liststockist")}
            />
          </div>
        </form>

        <div className="table-container">
          <h1 class="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
            <span class="text-blue-700">Stockist </span>
            <span class="text-blue-500">History</span>
          </h1>

          {formData.stockistName && (
            <table className="unlock-dcr-leave-table text-sm ">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th>S.No</th>
                  <th>Stockist</th>
                  <th>Contact Person</th>
                  <th>State</th>
                  <th>City</th>
                  <th>Mobile</th>
                  <th>Email</th>
                  <th>Mapping Code</th>
                  <th>GSTIN</th>
                  <th>Address</th>
                  <th>Lat, Long</th>
                  <th>Added Date</th>
                  <th>Updated By</th>
                  <th>Start Time</th>
                  <th>End Time</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td>{formData.stockistName}</td>
                  <td>{formData.contactPerson}</td>
                  <td>
                    {states.find((s) => s.id === formData.state)?.name || "N/A"}
                  </td>
                  <td>
                    {cities.find((c) => c.id === formData.city)?.name || "N/A"}
                  </td>
                  <td>{formData.mobile}</td>
                  <td>{formData.email || "N/A"}</td>
                  <td>{formData.mappingCode || "N/A"}</td>
                  <td>{formData.gstin}</td>
                  <td>{formData.address || "N/A"}</td>
                  <td>
                    {formData.latitude && formData.longitude
                      ? `${formData.latitude}, ${formData.longitude}`
                      : "N/A"}
                  </td>
                  <td>
                    {formData.added_date
                      ? new Date(formData.added_date).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }
                        )
                      : "N/A"}
                  </td>
                  <td>{formData.updated_by}</td>
                  <td>
                    {formData.start_time
                      ? new Date(formData.start_time).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }
                        )
                      : "N/A"}
                  </td>
                  <td>
                    {formData.end_time
                      ? new Date(formData.end_time).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }
                        )
                      : "N/A"}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
      {/* <Toaster /> */}
    </div>
  );
};

export default EditStockist;
